import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { connectSSE } from '../../api/sse';
import { createConversation, suggestProjectInfo } from '../../api/client';
import { summarizeToolResult } from '../../utils/feedSummarizer';
import { PLATFORM_TO_PROVIDER, PROVIDER_FETCH_TOOL } from '../../data/platforms';
import { useAuditStore } from '../../stores/useAuditStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { useOnboardingStore } from '../../stores/useOnboardingStore';
import { useStageGuard } from '../../hooks/useStageGuard';
import './onboarding.css';

/**
 * Step 3 — read everything the connected platforms will give us.
 *
 * This is the pass that makes the next screen possible: it runs the
 * `onboarding-collect` profile (fetchers only, ~30 days) so the Confirm step
 * has real data to describe the business from. Progress rows are driven by
 * actual `tool_start` / `tool_result` events, not a timer — a row only turns
 * green when its source really answered.
 */

const SUGGEST_TASK = 'suggest';
/** The web-research step's row. Keyed on the tool that ends it, so the same
 *  `onToolResult` join used for the fetchers marks it done. */
const RESEARCH_TASK = 'save_company_research';
/** Uploaded files are read through whichever tool fits the format;
 *  the row settles on the first of them to return. */
const FILES_TASK = 'load_raw_data';
/** Tool name -> checklist row. Four different tools can read an upload
 *  (the ingested source, the file itself, a targeted query), and the user
 *  should see one row for "your files", not four. */
function rowFor(tool: string): string {
  return FILE_TOOLS.has(tool) ? FILES_TASK : tool;
}

const FILE_TOOLS = new Set([
  'load_raw_data', 'list_uploaded_files', 'parse_uploaded_file',
  'query_uploaded_file',
]);

/**
 * The chat this onboarding session is building — the one collection fills and
 * the audit then runs in. Kept in sessionStorage because the Zustand store is
 * in-memory: a reload between /collect and /processing would otherwise lose it
 * and start the audit against an empty chat.
 */
function readStoredChatId(): string | null {
  try {
    return sessionStorage.getItem('collect_chat_id');
  } catch {
    return null; // private mode — the in-memory store covers the happy path
  }
}

interface Task {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'done' | 'skipped';
  detail?: string;
  /** When it started running — drives the in-flight share of the bar. */
  startedAt?: number;
}

/**
 * How "done" a task counts for. Settled tasks count whole; a running one earns
 * an asymptotic share of its own slice, so the bar keeps inching during a slow
 * fetch instead of sitting on a round number.
 *
 * The curve reaches ~0.63 of the slice at 12s and is capped below 1 — a task
 * that is still running must never read as finished.
 */
const RUNNING_HALF_LIFE_MS = 12_000;
const RUNNING_MAX_SHARE = 0.9;

function taskShare(task: Task, now: number): number {
  if (task.status === 'done' || task.status === 'skipped') return 1;
  if (task.status === 'running' && task.startedAt !== undefined) {
    const elapsed = Math.max(0, now - task.startedAt);
    return Math.min(RUNNING_MAX_SHARE, 1 - Math.exp(-elapsed / RUNNING_HALF_LIFE_MS));
  }
  return 0;
}

function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

export default function CollectView() {
  const navigate = useNavigate();
  // This screen has a side effect — it starts an agent run — so unlike the
  // other steps it must WAIT for the guard rather than merely render under it.
  // A user whose audit is already running or finished gets forwarded to
  // /processing or /report, and no second collection pass is started.
  const verdict = useStageGuard('/collect');
  const token = useAuthStore((s) => s.token);
  const connectedSources = useAuditStore((s) => s.connectedSources);
  const uploadedFiles = useAuditStore((s) => s.uploadedFiles);
  const setCollectChatId = useAuditStore((s) => s.setCollectChatId);
  const setSuggestion = useOnboardingStore((s) => s.setSuggestion);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  // Re-render on a timer so the in-flight share of the bar keeps advancing
  // between events. Without it the bar only moves when a tool finishes, which
  // on a single-source account means one jump from 0% to 50%.
  const [now, setNow] = useState(() => Date.now());
  const startedAtRef = useRef(Date.now());
  const highWaterRef = useRef(0);
  const cancelRef = useRef<(() => void) | null>(null);
  // The conversation is created once and shared by every run of the effect.
  // StrictMode invokes effects twice in development: a plain "have I started?"
  // ref would let the first pass claim the flag, get torn down by the cleanup,
  // and leave the second pass bailing out — the run would never start at all.
  // Caching the promise instead means both passes await the same chat, and
  // whichever pass survives goes on to open the stream.
  const chatPromiseRef = useRef<Promise<string> | null>(null);
  // `done` and `error` can both arrive for one run; without this the
  // suggestion would be requested twice and the navigation queued twice.
  const finishedRef = useRef(false);
  const mountedRef = useRef(true);
  const handoffRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const patch = useCallback((id: string, next: Partial<Task>) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...next } : t)));
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, []);

  // The suggestion pass is the point of this screen, so it runs whether the
  // agent finished cleanly or gave up — a partial fetch still beats an empty
  // Confirm form.
  const finish = useCallback(async (chatId: string) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setTasks((prev) =>
      prev.map((t) => (t.status === 'running' || t.status === 'pending') && t.id !== SUGGEST_TASK
        ? { ...t, status: 'skipped', detail: 'no data' }
        : t));
    patch(SUGGEST_TASK, { status: 'running', startedAt: Date.now() });
    try {
      const suggestion = await suggestProjectInfo(chatId);
      if (!mountedRef.current) return;
      setSuggestion(suggestion);
      patch(SUGGEST_TASK, { status: 'done', detail: 'ready' });
    } catch {
      if (!mountedRef.current) return;
      // Confirm can still render from stored details; don't strand the user.
      patch(SUGGEST_TASK, { status: 'skipped', detail: 'skipped' });
    }
    // A beat on 100% so the last row visibly lands before the screen changes.
    handoffRef.current = setTimeout(() => navigate('/confirm'), 600);
  }, [navigate, patch, setSuggestion]);

  useEffect(() => {
    // Nothing happens until the guard has spoken. 'checking' is the initial
    // state and 'redirecting' means we're on our way elsewhere — in both cases
    // starting the fetch would be work the user never asked for.
    if (verdict !== 'clear') return;

    // Re-armed on every run: the previous run's cleanup left these in their
    // torn-down state, and under StrictMode that previous run is the throwaway
    // first pass.
    mountedRef.current = true;
    finishedRef.current = false;

    const connected = connectedSources.filter((s) => s.status === 'connected');
    const withFetchers = connected.filter(
      (s) => PROVIDER_FETCH_TOOL[PLATFORM_TO_PROVIDER[s.id] ?? ''],
    );

    // Uploads are read by the pass too — server-side ingestion turns them
    // into sources before it starts, and the checklist has to say so or the
    // user watches a file they just uploaded go unmentioned.
    const hasUploads = uploadedFiles.length > 0;

    setTasks([
      ...withFetchers.map((s) => ({
        id: PROVIDER_FETCH_TOOL[PLATFORM_TO_PROVIDER[s.id]],
        label: `Reading your ${s.name} profile`,
        status: 'pending' as const,
      })),
      ...(hasUploads
        ? [{
            id: FILES_TASK,
            label: `Reading your uploaded file${uploadedFiles.length === 1 ? '' : 's'}`,
            status: 'pending' as const,
          }]
        : []),
      { id: RESEARCH_TASK, label: 'Researching your company online', status: 'pending' },
      { id: SUGGEST_TASK, label: 'Filling in your project details', status: 'pending' },
    ]);

    let cancelled = false;

    (async () => {
      try {
        // This creates the chat the AUDIT will run in. Collection is a separate
        // profile run (onboarding-collect), but it shares the chat so its
        // source_data lands exactly where the audit's `load_source()` looks —
        // no cross-chat plumbing. The separation is enforced on the server
        // instead: a pre-audit pass writes no analysis status and no analysis
        // events, so nothing reads its run back as the audit's own.
        if (!chatPromiseRef.current) {
          chatPromiseRef.current = (async () =>
            useAuditStore.getState().collectChatId
            // The store is in-memory: on a reload it is empty, and minting a
            // second chat here would strand everything already collected in
            // the first one.
            || readStoredChatId()
            || (await createConversation('Business Audit')).id)();
        }
        const chatId = await chatPromiseRef.current;
        if (cancelled) return;
        setCollectChatId(chatId);
        // The store is in-memory only; without this a refresh between here and
        // /processing would lose the chat holding the collected data, and the
        // audit would start in a fresh, empty one.
        try {
          sessionStorage.setItem('collect_chat_id', chatId);
        } catch { /* private mode — the in-memory store covers the happy path */ }

        // Genuinely nothing to read — no platform fetcher AND no upload. Only
        // then is skipping straight to the description honest; with files
        // present the pass has real work (ingested sources to read, and a
        // company to look up once it knows the name).
        if (withFetchers.length === 0 && !hasUploads) {
          await finish(chatId);
          return;
        }

        cancelRef.current = connectSSE(
          chatId,
          'Profile this business from every connected source, then research it online.',
          'onboarding-collect',
          {
            // A source that already finished stays finished. RevenueCat is
            // read twice (the profile, then the subscription overview), and
            // without this guard its second call re-opened a settled row —
            // so the checklist showed two spinners at once, the counter went
            // backwards, and the bar retreated. Later reads of a source we
            // have already counted are not new progress.
            onToolStart: ({ name }) =>
              setTasks((prev) => prev.map((t) =>
                t.id === rowFor(name) && t.status === 'pending'
                  ? { ...t, status: 'running' as const, startedAt: Date.now() }
                  : t)),
            onToolResult: ({ name, result, is_error }) => {
              const summary = summarizeToolResult(name, result, is_error);
              // Same rule on the way out: a repeat read may refresh the
              // detail line but must not turn a done row back into an error.
              setTasks((prev) => prev.map((t) => {
                if (t.id !== rowFor(name)) return t;
                if (t.status === 'done' && is_error) return t;
                return {
                  ...t,
                  status: is_error ? ('skipped' as const) : ('done' as const),
                  detail: is_error ? 'unavailable' : summary?.msg,
                };
              }));
              // The web-research step can't announce itself: `web_search` is a
              // hosted server tool, so its calls never reach us as tool events
              // — only the save at the end does. Start the row as soon as the
              // last platform settles, which is when the agent moves on to it.
              setTasks((prev) => {
                const fetchers = prev.filter(
                  (t) => t.id !== RESEARCH_TASK && t.id !== SUGGEST_TASK,
                );
                const allSettled = fetchers.length > 0 && fetchers.every(
                  (t) => t.status === 'done' || t.status === 'skipped',
                );
                if (!allSettled) return prev;
                return prev.map((t) =>
                  t.id === RESEARCH_TASK && t.status === 'pending'
                    ? { ...t, status: 'running' as const, startedAt: Date.now() }
                    : t);
              });
            },
            onDone: () => { if (!cancelled) void finish(chatId); },
            onError: ({ message }) => {
              if (cancelled) return;
              // A refused run is worth naming.
              if (message.includes('409')) {
                // The audit already started on this chat, so re-collecting
                // would rewrite the profile it was launched with. (A repeat
                // collect BEFORE the audit is allowed and never lands here.)
                // Nothing is broken — move on to Confirm.
                void finish(chatId);
                return;
              }
              setFailed(
                message.includes('403')
                  ? 'This account is not permitted to read data for this audit.'
                  : "We couldn't finish reading your sources.",
              );
              void finish(chatId);
            },
          },
          token ?? undefined,
        );
      } catch {
        if (!cancelled) setFailed("We couldn't start reading your sources.");
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      if (handoffRef.current) clearTimeout(handoffRef.current);
      cancelRef.current?.();
    };
  // Keyed on the verdict so the run starts the moment the guard clears, and
  // never before. The task list is a snapshot of what was connected on entry.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verdict]);

  // Read order, not connection order. The agent reads one source at a time
  // but picks its own order, so a checklist fixed to the order the platforms
  // were connected in shows the spinner above rows that already finished —
  // which reads as several sources being read at once. Sorting by the moment
  // each row actually started puts the one in flight at the bottom of the
  // finished ones, where a reader expects it. Rows that haven't started keep
  // their original order below.
  const orderedTasks = useMemo(() => {
    const order = new Map(tasks.map((t, i) => [t.id, i]));
    return [...tasks].sort((a, b) => {
      if (a.startedAt === undefined && b.startedAt === undefined) {
        return (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0);
      }
      if (a.startedAt === undefined) return 1;
      if (b.startedAt === undefined) return -1;
      return a.startedAt - b.startedAt;
    });
  }, [tasks]);

  const settled = tasks.filter((t) => t.status === 'done' || t.status === 'skipped').length;
  const rawPercent = tasks.length
    ? (tasks.reduce((sum, t) => sum + taskShare(t, now), 0) / tasks.length) * 100
    : 0;
  // Monotonic: a task flipping from running (partial credit) to skipped must
  // never make the bar retreat.
  highWaterRef.current = Math.max(highWaterRef.current, rawPercent);
  const percent = Math.round(highWaterRef.current);
  const done = settled === tasks.length && tasks.length > 0;

  // Before the guard clears, nothing has started and there is nothing to
  // report — showing the bar at 0% here would look exactly like a stalled run.
  if (verdict !== 'clear') {
    return (
      <div className="mx-auto flex max-w-[620px] flex-col items-center gap-3 px-4 py-24 text-center sm:px-8">
        <span className="size-5 animate-spin-ring rounded-full border-[1.5px] border-secondary border-t-transparent" />
        <p className="text-sm text-muted-foreground">Checking where you left off…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[620px] px-4 pb-20 pt-10 sm:px-8">
      <h1 className="mb-1 text-xl font-bold tracking-tight text-foreground">Reading your data</h1>
      <p className="mb-7 text-sm text-muted-foreground">
        Reading your connected sources one at a time, then looking your company up online.
      </p>

      <div className="flex items-end justify-between gap-4">
        <div className="text-[44px] font-bold leading-none tracking-tight tabular-nums text-foreground">
          {percent}%
        </div>
        <div className="pb-1 text-right font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
          <div>{formatElapsed(now - startedAtRef.current)} elapsed</div>
          <div className="mt-0.5 normal-case tracking-normal">
            {settled} of {tasks.length} sources read
          </div>
        </div>
      </div>

      {/* Same bar as the analysis screen — gradient fill, shimmer, and a
          glowing head that leads the edge while work is in flight. */}
      <div className="relative my-5 h-2 rounded-full bg-muted">
        <span className="ob-bar__fill" style={{ width: `${percent}%` }} />
        {!done && !failed && (
          <span className="ob-bar__head" style={{ right: `${100 - percent}%` }} />
        )}
      </div>

      <div className="flex flex-col">
        {orderedTasks.map((task) => (
          <div
            key={task.id}
            className={cn(
              'flex items-center gap-3 border-b py-2.5 text-sm transition-colors last:border-b-0',
              task.status === 'pending' && 'text-muted-foreground',
              task.status === 'running' && 'font-semibold text-foreground',
              (task.status === 'done' || task.status === 'skipped') && 'text-foreground',
            )}
          >
            <span
              className={cn(
                'flex size-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px]',
                task.status === 'pending' && 'border-dashed border-border',
                task.status === 'running' && 'animate-spin-ring border-secondary border-t-transparent',
                task.status === 'done' && 'border-success bg-success',
                task.status === 'skipped' && 'border-border bg-muted',
              )}
            >
              {task.status === 'done' && <Check className="size-3 text-success-foreground" />}
              {task.status === 'skipped' && (
                <span className="block size-1 rounded-full bg-muted-foreground/50" />
              )}
            </span>
            <span className="min-w-0 flex-1 truncate">{task.label}</span>
            {task.detail && (
              // Capped so a chatty tool summary can't squeeze out the label.
              <span className="max-w-[45%] truncate text-xs tabular-nums text-muted-foreground">
                {task.detail}
              </span>
            )}
          </div>
        ))}
      </div>

      {uploadedFiles.length > 0 && (
        <p className="mt-5 text-xs text-muted-foreground">
          {uploadedFiles.length} uploaded file{uploadedFiles.length === 1 ? '' : 's'} will be read
          during the analysis.
        </p>
      )}

      {failed && (
        <div className="mt-6 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {failed}
          <Button
            variant="link"
            className="h-auto cursor-pointer p-0 pl-2 text-sm text-destructive underline"
            onClick={() => navigate('/connect')}
          >
            Back to connections
          </Button>
        </div>
      )}
    </div>
  );
}
